<script>
import { useAlert } from 'dashboard/composables';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import ErrorState from 'dashboard/components/widgets/ErrorState.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import OverrideCard from './OverrideCard.vue';
import createOverrideModal from './createOverrideModal.vue';
import DeleteModal from 'dashboard/components/dahreply/DeleteModal.vue';

export default {
  name: "OverrideSection",
  props: {
    scheduleId: {
      type: String,
      default: null,
    },
  },
  components: {
    LoadingState,
    ErrorState,
    EmptyState,
    OverrideCard,
    createOverrideModal,
    DeleteModal,
  },
  data() {
    return {
      showCreateModal: false,
      showDeleteModal: false,
      deleteId: null,
      deleteName: null,
      isDeleting: false,
    };
  },
  computed: {
    overrideList() {
      return this.$store.state.override.overrides || [];
    },
    isLoading() {
      return this.$store.state.override.isLoading || false;
    },
    isError() {
      return this.$store.state.override.isError || false;
    },
  },
  methods: {
    async fetchOverrides(scheduleId) {
      try {
        await this.$store.dispatch(
          'override/fetchOverrides',
          scheduleId
        );
      } catch (err) {
        console.error('Error loading override:', err);
        useAlert(err);
      }
    },
    handleOpenDelete({ id, name }) {
      this.showDeleteModal = true;
      this.deleteId = id;
      this.deleteName = name;
    },
    handleCloseDelete() { 
      this.showDeleteModal = false;
      this.deleteId = null;
      this.deleteName = null;
    },
    async handleConfirmDelete() {
      try {
        this.isDeleting = true;
        await this.$store.dispatch('override/deleteOverride', {
          schedule_id: this.scheduleId,
          overrideId: this.deleteId
        });
        useAlert('Override deleted successfully.');
        await this.fetchOverrides(this.scheduleId);
        this.handleCloseDelete();
      } catch (err) {
        console.error('Error deleting override:', err);
        useAlert(err.message || 'Failed to delete override.');
      } finally {
        this.isDeleting = false;
      }
    },
    async refreshPage() {
      await this.fetchOverrides(this.scheduleId);
    }
  },
  watch: {
    async scheduleId(newVal) {
      if (newVal) await this.fetchOverrides(newVal);
    },
  },
  mounted() {
    if (this.scheduleId) this.fetchOverrides(this.scheduleId);
  },
};
</script>

<template>
  <div class="p-4 bg-white dark:bg-slate-700 rounded-md border-2 border-slate-100 dark:border-slate-50">
    <!-- Modals -->
    <createOverrideModal 
      v-if="showCreateModal"
      :scheduleId="scheduleId" 
      @close="showCreateModal = false" 
      @refresh="refreshPage" 
    />
    <DeleteModal
      v-if="showDeleteModal"
      :delete-name="deleteName"
      :is-loading="isDeleting"
      @confirm="handleConfirmDelete"
      @close="handleCloseDelete"
    />

    <!-- Headers -->
    <div class="flex justify-between items-center mb-4">
      <h1 class="text-xl font-semibold text-gray-900 dark:text-gray-100">Schedule Overrides</h1>
      <woot-button
        variant="smooth"
        color-scheme="primary"
        icon="plus-sign"
        @click="showCreateModal = true"
        :disabled="!scheduleId"
      >
        Create Override
      </woot-button>
    </div> 

    <!-- States -->
    <LoadingState v-if="isLoading" message="Loading overrides..." />

    <ErrorState v-else-if="!isLoading && isError" />

    <EmptyState
      v-else-if="!isLoading && !isError && overrideList.length === 0"
      title="No overrides Found"
      message="Create a new override to get started"
    />

    <!-- Override List -->
    <div v-else>
      <OverrideCard 
        v-for="item in overrideList"
        :key="item.id"
        :override="item"
        @delete="handleOpenDelete"
      />
    </div>
  </div>
</template>